/**
 * Publisher Workspace (P30 / PENDING_TODO A5.5).
 *
 * Lists every server in the registry the caller's WGs own. Click a row to
 * jump to the Server Detail page.
 */
import { useNavigate, Link } from 'react-router-dom';
import type { JSX } from 'react';
import { Plus, ServerCog } from 'lucide-react';

import { useServers } from '@/api/index.js';
import { ActionButton } from '@/components/console/ActionButton.js';
import { DataTable, type DataRow } from '@/components/console/DataTable.js';
import { PageHeader } from '@/components/console/PageHeader.js';
import { SectionCard } from '@/components/console/SectionCard.js';
import { AsyncShell } from '@/components/console/StateBlocks.js';

export default function PublisherWorkspace(): JSX.Element {
  const navigate = useNavigate();
  const all = useServers();

  const rows: DataRow[] =
    all.data?.items.map((s) => ({
      id: s.id,
      slug: s.slug,
      name: s.name,
      owner: s.owningWgId,
      backend: s.backendUrl,
      risk: s.riskTier,
      status: s.status,
    })) ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Build"
        title="Publisher Workspace"
        description="Servers your workgroups own. Click a row to see lifecycle, tools, credentials, and approval history. New publishers can register a server via the wizard."
      >
        <Link to="/build/register">
          <ActionButton icon={Plus} primary>
            Register server
          </ActionButton>
        </Link>
      </PageHeader>

      <SectionCard
        icon={ServerCog}
        title={`My servers (${rows.length})`}
        text="Includes draft + in_review + active + retired. Filter via the catalog page for the public-facing slice."
      >
        <AsyncShell
          isLoading={all.isLoading}
          isError={all.isError}
          isEmpty={rows.length === 0}
          error={all.error}
          loadingTitle="Loading servers"
          emptyTitle="No servers in the registry yet"
          emptyHint={
            <>
              Use <Link to="/build/register">Register Server</Link> to add the first one.
            </>
          }
          errorTitle="Could not load servers"
          errorHint="The gateway is unreachable from this browser. Hard-refresh (Ctrl+Shift+R); if it persists, the gateway service may be down."
        >
          <DataTable
            columns={['slug', 'name', 'owner', 'backend', 'risk', 'status']}
            rows={rows}
            onRow={(row) => navigate(`/build/servers/${row.id}`)}
          />
        </AsyncShell>
      </SectionCard>
    </>
  );
}
